import { uniq } from "lodash";
import StyledInput from "notes-and-forms-v2/components/styled-input/StyledInput";
import getClasses from "utils/getClasses";
import {
  ElementViewForClipboardProps,
  ElementViewProps,
} from "../AtomicElementView";
import styles from "./CheckboxesGridContent.module.scss";

type CheckboxesGridData = Record<string, string[]>;

function parseAsArray(data: any) {
  if (!data) {
    return [];
  }

  return Array.isArray(data) ? data : [data.toString()];
}

function parseRawData(
  data: any,
  element: CheckboxesGridElement,
): CheckboxesGridData {
  if (!data || typeof data !== "object") {
    return {};
  }

  const mappedEntries = Object.entries(data).map(([key, value]) => {
    const row = element.rows.find((row) => row.title === key || row.id === key);
    const valueAsArray = parseAsArray(value);
    const columns = valueAsArray.map((el) =>
      element.columns.find((column) => column.title === el || column.id === el),
    );

    if (!row || !columns) {
      return [];
    }

    return [row?.title, columns.map((column) => column?.title)];
  });

  const validMappedEntries = mappedEntries.filter((entry) => entry.length);

  return Object.fromEntries(validMappedEntries);
}

export function CheckboxesGridContentForClipboard({
  element,
  data: rawData,
}: ElementViewForClipboardProps<CheckboxesGridElement>) {
  const data = parseRawData(rawData, element);
  return (
    <ul>
      {element.rows.map((row) => (
        <li key={row.id}>
          {row.title}: {data[row.title]?.join(", ") || "No answer"}
        </li>
      ))}
    </ul>
  );
}

function getFilteredColumns(
  element: CheckboxesGridElement,
  options: TemplateContentOptions,
  data: CheckboxesGridData,
) {
  if (!options.hideUnselectedOptionsForCheckboxes) {
    return element.columns;
  }

  const optionsSelectedLeastOnce = uniq(Object.values(data).flat());
  return element.columns.filter((column) =>
    optionsSelectedLeastOnce.includes(column.title),
  );
}

export default function CheckboxesGridContent({
  data: rawData,
  element,
  onChange,
  isLocked,
  isPrintMode,
  isDownloadMode,
  disabled,
  options = {},
  showAsMissing,
}: ElementViewProps<CheckboxesGridElement>) {
  const data = parseRawData(rawData, element);

  const filteredColumns = getFilteredColumns(element, options, data);

  return (
    <div className={styles.container}>
      <div
        className={styles["answers-grid"]}
        style={{
          gridTemplateColumns: `2fr repeat(${filteredColumns.length},1fr)`,
        }}
      >
        <div
          className={getClasses(
            styles["grid-row-wrapper"],
            isPrintMode && styles.print,
            "template-solid-bg",
          )}
        >
          <div className={styles["top-left-cell"]}>{/* Empty cell */}</div>
          {filteredColumns.map((column) => (
            <div key={column.id}>{column.title}</div>
          ))}
        </div>

        {element.rows.map((row) => (
          <div
            key={row.id}
            className={getClasses(
              styles["grid-row-wrapper"],
              isPrintMode && styles.print,
              showAsMissing && !data[row.title]?.length && styles.missing,
            )}
          >
            <div className={styles["grid-row-title"]}>{row.title}</div>
            {filteredColumns.map((column) => (
              <label key={column.id} htmlFor={[row.id, column.id].join("-")}>
                <StyledInput
                  id={[row.id, column.id].join("-")}
                  type="checkbox"
                  checked={(data[row.title] ?? []).includes(column.title)}
                  onChange={(event) => {
                    const previousRowData = data[row.title] ?? [];
                    if (event.target.checked) {
                      onChange({
                        ...data,
                        [row.title]: [...previousRowData, column.title],
                      });
                    } else {
                      onChange({
                        ...data,
                        [row.title]: previousRowData.filter(
                          (el) => el !== column.title,
                        ),
                      });
                    }
                  }}
                  disabled={disabled || isLocked}
                  disableStyling={isDownloadMode}
                />
              </label>
            ))}
          </div>
        ))}
      </div>

      {!isDownloadMode && !options?.disableStickyRowHeaderForGrids && (
        <div
          className={getClasses(styles["answers-grid"], styles["grid-copy"])}
          style={{
            gridTemplateColumns: `2fr repeat(${filteredColumns.length},1fr)`,
          }}
        >
          <div
            className={getClasses(
              styles["grid-row-wrapper"],
              isPrintMode && styles.print,
              "template-solid-bg",
            )}
          >
            <div className={styles["top-left-cell"]}>{/* Empty cell */}</div>
            {filteredColumns.map((column) => (
              <div key={column.id}>{column.title}</div>
            ))}
          </div>

          {element.rows.map((row) => (
            <div
              key={row.id}
              className={getClasses(
                styles["grid-row-wrapper"],
                isPrintMode && styles.print,
                showAsMissing && !data[row.title]?.length && styles.missing,
              )}
            >
              <div className={styles["grid-row-title"]}>{row.title}</div>
              {filteredColumns.map((column) => (
                <label key={column.id}>
                  <StyledInput type="checkbox" disabled={true} />
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
